"use client"

import { Activity, BadgeCheck, ChevronsUpDown, Coins, CreditCard, LogOut, Users } from "lucide-react"
import { useState } from "react"
import * as React from "react"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu"
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "../ui/sidebar"
import { clearAuthToken, getAuthToken, getAccountApiUrl, getAccountAppUrl, redirectToLoginApp, useSidebarUser } from "../lib/auth"
import { getAccounts, removeAccount } from "../lib/multi-account"
import { SwitchAccountsDialog } from "./switch-accounts-dialog"

export function NavUser({
  user: userProp,
}: {
  user: {
    name: string
    email: string
    avatar: string
  }
}) {
  const { isMobile } = useSidebar()
  const [switchOpen, setSwitchOpen] = useState(false)
  const sidebarUser = useSidebarUser()

  const user = sidebarUser ?? userProp

  /** Sign out of the active account on this device.
   *  - If there are other accounts in the device list, switch to the most
   *    recently added remaining one (no full redirect — just reload).
   *  - Otherwise, full logout: hit /api/auth/logout, clear cookies, redirect
   *    to the login app.
   */
  const handleLogout = async () => {
    const token = getAuthToken()
    const accounts = getAccounts()

    if (accounts.length > 1) {
      // Multi-account: remove just this one. removeAccount handles the
      // server revoke + active switch + reload.
      const active = accounts.find(a => a.token === token) ?? accounts[accounts.length - 1]
      const result = await removeAccount(active.id)
      if (result.removed && result.nextActiveId) {
        if (typeof window !== "undefined") {
          setTimeout(() => window.location.reload(), 100)
        }
        return
      }
    }

    // Single-account or removeAccount failed → full logout.
    //
    // SECURITY V-029 FIX: the JWT now lives in an HttpOnly cookie, so
    // getAuthToken() returns null under normal cookie-auth. The old code
    // gated the server /logout call on `if (token)`, which meant the call
    // was SKIPPED — and clearAuthToken() can only expire JS-readable
    // cookies, never the HttpOnly AskFinz_auth_token/access/refresh ones.
    // The result was a cosmetic logout: the server session survived and
    // middleware (which checks the auth cookie first) let the user back into
    // every app. We now ALWAYS hit /api/auth/logout and authenticate it with
    // the HttpOnly cookie via credentials:'include'; the Bearer header is
    // added only when a JS-readable token actually exists (legacy/multi-acct).
    //
    // Call the server BEFORE clearAuthToken() so the session + CSRF cookies
    // are still intact when the request is processed.
    try {
      // SECURITY V-028: /logout requires the X-CSRF-Token header matching the
      // value the server set in the AskFinz_csrf cookie at login. Read it from
      // document.cookie (NOT HttpOnly) and echo it into the request header.
      let csrf = ""
      if (typeof document !== "undefined") {
        const m = document.cookie.match(/(?:^|;\s*)AskFinz_csrf=([^;]+)/)
        csrf = m ? decodeURIComponent(m[1]) : ""
      }
      const headers: Record<string, string> = {}
      if (token) headers["Authorization"] = `Bearer ${token}`
      if (csrf) headers["X-CSRF-Token"] = csrf
      await fetch(`${getAccountApiUrl()}/api/auth/logout`, {
        method: "POST",
        headers,
        credentials: "include",
      })
    } catch (error) {
      console.error("Logout API call failed:", error)
    }
    // Clear the domain-wide session marker AFTER the server wipes the HttpOnly
    // cookies, so other tabs also see the logout immediately.
    clearAuthToken()
    redirectToLoginApp()
  }

  const goTo = (path: string) => {
    if (typeof window !== "undefined") {
      window.location.href = `${getAccountAppUrl()}${path}`
    }
  }

  const getInitials = (name: string) => {
    const parts = name.split(" ")
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  return (
    <SidebarMenu className="mb-0">
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              tooltip={user.name}
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:items-center h-10 group-data-[collapsible=icon]:!overflow-visible group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:!rounded-md"
            >
              <div className="flex items-center justify-center shrink-0">
                <Avatar className="h-7 w-7 rounded-full group-data-[collapsible=icon]:!h-6 group-data-[collapsible=icon]:!w-6 flex-shrink-0 bg-white">
                  <AvatarImage
                    src={user.avatar || ""}
                    alt={user.name}
                    className="object-cover w-full h-full rounded-full"
                  />
                  <AvatarFallback className="rounded-full text-xs group-data-[collapsible=icon]:text-[10px]">{getInitials(user.name)}</AvatarFallback>
                </Avatar>
              </div>
              <div className="grid flex-1 text-left text-xs leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-medium">{user.name}</span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-3.5 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-full bg-white">
                  <AvatarImage
                    src={user.avatar || "/placeholder.svg"}
                    alt={user.name}
                  />
                  <AvatarFallback className="rounded-full">{getInitials(user.name)}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onSelect={() => goTo("/usage")}>
                <Activity className="size-4" />
                Usage
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => goTo("/credits")}>
                <Coins className="size-4" />
                Credits
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => goTo("/billing")}>
                <CreditCard className="size-4" />
                Billing
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setSwitchOpen(true) }}>
                <Users className="size-4" />
                Switch Accounts
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => goTo("/account")}>
                <BadgeCheck className="size-4" />
                Account
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={(e) => {
              e.preventDefault()
              handleLogout()
            }}>
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>

      <SwitchAccountsDialog open={switchOpen} onOpenChange={setSwitchOpen} />
    </SidebarMenu>
  )
}
